import { NEW_LANDINGI_URL } from '@config/env'
import { BillingPeriodType } from '@constants/billingPeriods'
import { PLANS } from '@constants/plans'
import { useFeatureFlags } from '@contexts/featureFlags'
import { useUserContext } from '@contexts/user'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { isPlanCheaper } from '@helpers/plans'
import { wait } from '@helpers/promise'
import {
  Button,
  Divider,
  emitTimingToastToggle,
  Icon,
  Modal,
  Paragraph,
  Spacer
} from '@landingi/landingi-ui-kit'
import client from '@lib/http/client'
import {
  CHANGE_BILLING_CYCLE,
  DOWNGRADE,
  UPGRADE
} from '@pages/Pricing/routes/AccountPackage/components/PlanPanels/components/planCTAs'
import { useAccountPackageContext } from '@pages/Pricing/routes/AccountPackage/contexts'
import { changePlan } from '@services/pricing/changePlan'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'simple-flexbox'
import useSWRImmutable from 'swr/immutable'

import {
  // TODO - Pricing II iteration
  // AdditionalDiscount,
  AddonsLimitExceededAlert,
  BillingCycle,
  ChangePlanWithAddons,
  ChosenPlan,
  DowngradeFromAgencyAlert,
  PlansLimits,
  TotalDueToday,
  TotalDueTodayFree,
  UpgradePlanAlert
} from './components'
import { buttons } from './components/Buttons'
import {
  TotalDueTodayFreePromotion3M,
  TotalDueTodayFreePromotion12M,
  TotalDueTodayFreePromotionForFreePlans,
  TotalDueTodayPromotion12M
} from './components/TotalDue'
import { PlansLimitsKeys } from './plansLimits'
import styles from './SummarySubscriptionModal.module.scss'

const packageInfoFetcher = ({
  url,
  params
}: {
  url: string
  params: { recordKey: string; paymentPeriod: number }
}) => {
  return client
    .get(url, {
      params
    })
    .then(res => res.data)
}

const { AGENCY_22, AGENCY_21 } = PLANS

interface SummarySubscriptionModalProps {
  planRecordKey: PlansLimitsKeys
  buttonType?: string
  chosenBillingPeriodFromAddonStore?: BillingPeriodType
}
const SummarySubscriptionModal =
  NiceModal.create<SummarySubscriptionModalProps>(
    ({ planRecordKey, buttonType, chosenBillingPeriodFromAddonStore }) => {
      const { t } = useTranslation()
      const { isAgency, hasSubscription, hasFreeTrial } = useUserContext()

      const {
        getPackageNameForPlan,
        accountCurrency,
        getPackagePriceForPlan,
        chosenBillingPeriod: chosenBillingPeriodFromAccountPackage,
        getPackagePriceForPlanInMonths,
        getActiveDiscountForPlan,
        getAnnualSavingForPlan,
        hasUserExceededLimitForPlan,
        hasAccessToPromotion3M,
        hasAccessToPromotion12M,
        hasAccessToPromotionForFreePlans
      } = useAccountPackageContext()

      const chosenBillingPeriod =
        chosenBillingPeriodFromAddonStore ??
        chosenBillingPeriodFromAccountPackage

      const price = getPackagePriceForPlan(planRecordKey)
      const priceInMonths = getPackagePriceForPlanInMonths(planRecordKey)
      const packageName = getPackageNameForPlan(planRecordKey)
      const discount = getActiveDiscountForPlan(planRecordKey)
      const annualSaving = getAnnualSavingForPlan(planRecordKey)

      const pricing = useSWRImmutable(
        {
          url: 'payments/change-package-on-account',
          params: {
            recordKey: planRecordKey,
            paymentPeriod: chosenBillingPeriod
          }
        },
        packageInfoFetcher
      )?.data

      const { nextPaymentDate, subaccountsCount, hasLimitAddons } = {
        nextPaymentDate: pricing?.nextPaymentDate,
        subaccountsCount: pricing?.subaccountsCount,
        hasLimitAddons: pricing?.hasLimitAddons
      }

      const hasFreePlanRestrictions = useFeatureFlags('FREE_PLAN_RESTRICTIONS')
      const hasAccessToPricing2022 = useFeatureFlags('PRICING_2022')

      const hasUserExceededLimit = hasUserExceededLimitForPlan(planRecordKey)

      const { remove } = useModal()

      const [isLoading, setIsLoading] = useState(false)

      const handleButtonClick = async () => {
        try {
          setIsLoading(true)

          await changePlan({
            recordKey: planRecordKey,
            paymentPeriod: chosenBillingPeriod
          })

          // wait so package is changed for sure after redirection to /payments page
          await wait(2200)

          emitTimingToastToggle(t('change.plan.success'), 'success')

          setIsLoading(false)

          await wait(800)

          if (hasSubscription) {
            window.open(
              `https://${NEW_LANDINGI_URL}/payments/subscription`,
              '_self'
            )
          } else {
            window.open(`https://${NEW_LANDINGI_URL}/payments`, '_self')
          }
        } catch {
          emitTimingToastToggle(t('error.page.generic.title'), 'alert')
        }
      }

      const shouldShowUpgradePlanAlert =
        buttonType === UPGRADE && hasSubscription && !hasFreeTrial

      const shouldShowDowngradeFromAgencyAlert =
        isAgency &&
        isPlanCheaper({
          plan1: planRecordKey,
          plan2: hasAccessToPricing2022 ? AGENCY_22 : AGENCY_21,
          isOldPlan: !hasAccessToPricing2022
        }) &&
        subaccountsCount > 0

      const shouldShowChangePlanWithAddonsAlert =
        hasLimitAddons && (buttonType === UPGRADE || buttonType === DOWNGRADE)

      const getButton = () => {
        if (hasFreePlanRestrictions) {
          return 'startSubscription'
        }

        if (buttonType === CHANGE_BILLING_CYCLE) {
          return 'changeBillingCycle'
        }

        return 'changePlan'
      }

      const CTAButton = buttons[getButton()]

      const getTotalDueTodayFree = () => {
        if (hasAccessToPromotion3M(planRecordKey)) {
          return (
            <TotalDueTodayFreePromotion3M
              price={price}
              currency={accountCurrency}
              planRecordKey={planRecordKey}
            />
          )
        }

        if (hasAccessToPromotion12M(planRecordKey)) {
          return (
            <TotalDueTodayFreePromotion12M
              price={price}
              currency={accountCurrency}
              planRecordKey={planRecordKey}
              priceInMonths={priceInMonths}
              billingPeriod={chosenBillingPeriod}
            />
          )
        }

        if (hasAccessToPromotionForFreePlans) {
          return (
            <TotalDueTodayFreePromotionForFreePlans
              price={price}
              currency={accountCurrency}
              planRecordKey={planRecordKey}
            />
          )
        }

        return <TotalDueTodayFree price={price} currency={accountCurrency} />
      }

      const getTotalDueToday = () => {
        if (hasAccessToPromotion12M(planRecordKey)) {
          return (
            <TotalDueTodayPromotion12M
              price={price}
              currency={accountCurrency}
              planRecordKey={planRecordKey}
              priceInMonths={priceInMonths}
              billingPeriod={chosenBillingPeriod}
              date={nextPaymentDate}
            />
          )
        }

        return (
          <TotalDueToday
            price={price}
            priceInMonths={priceInMonths}
            currency={accountCurrency}
            billingPeriod={chosenBillingPeriod}
            discount={discount}
            date={nextPaymentDate}
          />
        )
      }

      return (
        <Modal
          isActive
          onClick={remove}
          i18n={{
            title: t('change.plan.page.modal.subscription.summary')
          }}
          className={styles['summary-subscription-modal']}
        >
          <ChosenPlan packageName={packageName} />

          <Spacer space='small' />

          <Divider />

          <Spacer space='small' />

          <BillingCycle
            billingPeriod={chosenBillingPeriod}
            currency={accountCurrency}
            annualSaving={annualSaving}
            shouldDisplayPromotion3MBadge={hasAccessToPromotion3M(
              planRecordKey
            )}
            shouldDisplayPromotion1MBadge={hasAccessToPromotionForFreePlans}
          />

          {/* TODO - Pricing II iteration */}
          {/* {discount && (
            <Fragment>
              <Spacer space='small' />

              <Divider />

              <Spacer space='small' />

              <AdditionalDiscount
                discount={discount}
                currency={accountCurrency}
              />
            </Fragment>
          )} */}

          <Spacer space='small' />

          <Divider />

          <Spacer space='small' />

          <PlansLimits planRecordKey={planRecordKey} />

          <Spacer space='small' />

          {hasUserExceededLimit && (
            <Fragment>
              <AddonsLimitExceededAlert packageName={packageName} />

              <Spacer space='small' />
            </Fragment>
          )}

          <Divider />

          <Spacer space='small' />

          {!hasFreePlanRestrictions && getTotalDueToday()}

          {hasFreePlanRestrictions && getTotalDueTodayFree()}

          <Spacer />

          {shouldShowUpgradePlanAlert && (
            <Fragment>
              <UpgradePlanAlert date={nextPaymentDate} />

              <Spacer space='small' />
            </Fragment>
          )}

          {shouldShowDowngradeFromAgencyAlert && (
            <Fragment>
              <DowngradeFromAgencyAlert subaccountsCount={subaccountsCount} />

              <Spacer space='small' />
            </Fragment>
          )}

          {shouldShowChangePlanWithAddonsAlert && (
            <Fragment>
              <ChangePlanWithAddons />

              <Spacer space='small' />
            </Fragment>
          )}

          <Row>
            <Icon
              icon='icon-shield'
              className={styles['summary-subscription-modal__icon-shield']}
            />

            <Paragraph size={12} padding='none'>
              {t('registration.flow.credit.card.step.30.day.money.back')}
            </Paragraph>
          </Row>

          <Spacer space='small' />

          <Row justifyContent='flex-end'>
            <Button onClick={remove} variant='secondary'>
              {t('label.cancel')}
            </Button>

            <CTAButton
              onClick={handleButtonClick}
              isLoading={isLoading}
              isDisabled={shouldShowDowngradeFromAgencyAlert}
            />
          </Row>
        </Modal>
      )
    }
  )

SummarySubscriptionModal.displayName = 'SummarySubscriptionModal'

export default SummarySubscriptionModal
